import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { ButtonBase, CircularProgress } from '@mui/material';
import { canUseDOM, getUserDetails, getWindow } from '@surfline/web-common';
import { AddIcon } from 'components/Icons/AddIcon';
import PillCheckIcon from 'components/Icons/PillCheckIcon';
import { useAppDispatch, useAppSelector } from '../../stores/hooks';
import { useSpotIsFavorite } from '../../hooks/useSpotIsFavorite';
import { doFavoriteSpot, doUnfavoriteSpot } from '../../actions/user';
import config from '../../config';
import styles from './FavoriteButton.module.scss';

interface Props {
  spotId: string;
  showLabel?: boolean;
}

const FavoritePillButton: React.FC<Props> = ({ spotId, showLabel = false }) => {
  const dispatch = useAppDispatch();
  const isSpotFavorited = useSpotIsFavorite(spotId);
  const userDetails = useAppSelector(getUserDetails);
  const router = useRouter();

  const [isAddingFavorite, setIsAddingFavorite] = useState(false);
  const [isRemovingFavorite, setIsRemovingFavorite] = useState(false);
  const [justFavorited, setJustFavorited] = useState(false);
  const justFavoritedTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (isAddingFavorite && isSpotFavorited) {
      setIsAddingFavorite(false);
      setJustFavorited(true);
      clearTimeout(justFavoritedTimer.current);
      justFavoritedTimer.current = setTimeout(() => setJustFavorited(false), 2000);
    }
    if (isRemovingFavorite && !isSpotFavorited) {
      setIsRemovingFavorite(false);
      setJustFavorited(false);
      clearTimeout(justFavoritedTimer.current);
    }
  }, [isSpotFavorited, isAddingFavorite, isRemovingFavorite]);

  useEffect(() => () => clearTimeout(justFavoritedTimer.current), []);

  const handleClick = () => {
    if (userDetails) {
      if (isSpotFavorited) {
        setIsRemovingFavorite(true);
        dispatch(doUnfavoriteSpot(spotId));
      } else {
        setIsAddingFavorite(true);
        dispatch(doFavoriteSpot(spotId));
      }
    } else if (canUseDOM) {
      const win = getWindow();
      const redirectUrl = `${config.surflineHost}${router.asPath}`;
      win.location.href = `${
        config.createAccountUrl
      }?alert=noAuthFav&redirectUrl=${encodeURIComponent(redirectUrl)}`;
    }
  };

  const isEffectivelyFavorited = !!userDetails && isSpotFavorited;

  const buttonIcon = useMemo(() => {
    if (isAddingFavorite || isRemovingFavorite) {
      return (
        <CircularProgress size={14} sx={{ color: 'white' }} data-testid="favorite-button-loading" />
      );
    }
    if (isEffectivelyFavorited) return <PillCheckIcon />;
    return <AddIcon showCircle={false} />;
  }, [isEffectivelyFavorited, isAddingFavorite, isRemovingFavorite]);

  const buttonClassName = classNames(styles.pillButton, {
    [styles.pillButtonActive]: isEffectivelyFavorited,
    [styles.pillButtonExpanded]: justFavorited || showLabel,
  });

  return (
    <ButtonBase
      className={buttonClassName}
      data-testid="favorite-button"
      disabled={isAddingFavorite || isRemovingFavorite}
      disableRipple
      disableTouchRipple
      onClick={(e) => {
        e.stopPropagation();
        handleClick();
      }}
      aria-label="Add to favorites"
      aria-pressed={isEffectivelyFavorited}
    >
      {buttonIcon}
      <span
        className={classNames(styles.pillLabel, {
          [styles.pillLabelExpanded]: justFavorited || showLabel,
        })}
      >
        Add to favorites
      </span>
    </ButtonBase>
  );
};

export default FavoritePillButton;
